// Komponen UI dasar CMS (desktop-first, tetap rapi di tablet).
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId, useState } from 'react';

import { errorMessage } from '../lib/api';
import { STATUS_LABEL } from '../lib/labels';

export function PageHeader({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pb-1">
      <div>
        <h1 className="text-2xl font-black tracking-tight text-slate-900">{title}</h1>
        {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
      </div>
      {children ? <div className="flex flex-wrap items-center gap-2.5">{children}</div> : null}
    </div>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  children,
  className = '',
  testId,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <section
      className={`rounded-2xl bg-white p-5 sm:p-6 border border-slate-200/80 shadow-xs transition-shadow ${className}`}
      data-testid={testId}
    >
      {title || actions ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3.5">
          <div>
            {title ? <h2 className="text-base font-bold text-slate-900 tracking-tight">{title}</h2> : null}
            {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'blue',
  className = '',
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: ReactNode;
  tone?: 'blue' | 'purple' | 'emerald' | 'amber' | 'red' | 'indigo';
  className?: string;
}) {
  const tones = {
    blue: 'bg-blue-50 text-[#3851F0] border-blue-100',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    red: 'bg-red-50 text-red-600 border-red-100',
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-white p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="text-2xl font-black tabular-nums text-slate-900 tracking-tight">{value}</p>
        </div>
        {icon ? (
          <div
            className={`flex size-11 shrink-0 items-center justify-center rounded-xl border ${tones[tone]} shadow-xs`}
          >
            {icon}
          </div>
        ) : null}
      </div>
      {hint ? (
        <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <span>{hint}</span>
        </div>
      ) : null}
    </div>
  );
}

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
const VARIANT: Record<Variant, string> = {
  primary:
    'bg-[#3851F0] text-white hover:bg-[#2B65EE] shadow-xs active:scale-[0.99] disabled:opacity-50 disabled:shadow-none',
  secondary:
    'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 shadow-xs active:scale-[0.99] disabled:opacity-50',
  danger: 'bg-[#E43437] text-white hover:bg-red-700 shadow-xs active:scale-[0.99] disabled:opacity-50',
  ghost: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50',
};

export function Button({
  variant = 'primary',
  loading = false,
  className = '',
  disabled,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean }) {
  return (
    <button
      type="button"
      className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-4 text-xs font-bold transition select-none cursor-pointer ${VARIANT[variant]} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : null}
      {children}
    </button>
  );
}

const inputClass =
  'block min-h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 shadow-2xs focus:border-[#3851F0] focus:ring-4 focus:ring-[#3851F0]/10 focus:outline-none transition aria-invalid:border-red-500 aria-invalid:focus:ring-red-500/10';

export function Field({
  label,
  error,
  hint,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string | undefined; hint?: string }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-xs font-bold uppercase tracking-wider text-slate-600">
        {label}
      </label>
      <input
        id={id}
        className={inputClass}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${id}-d` : undefined}
        {...rest}
      />
      {error ? (
        <p id={`${id}-d`} className="text-xs font-medium text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-d`} className="text-xs text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function Select({
  label,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-xs font-bold uppercase tracking-wider text-slate-600">
        {label}
      </label>
      <select id={id} className={inputClass} {...rest}>
        {children}
      </select>
    </div>
  );
}

export function TextArea({
  label,
  error,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; error?: string | undefined }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-xs font-bold uppercase tracking-wider text-slate-600">
        {label}
      </label>
      <textarea
        id={id}
        className={`${inputClass} py-2.5 min-h-24`}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error ? <p className="text-xs font-medium text-red-600">{error}</p> : null}
    </div>
  );
}

export function Loading({ label = 'Memuat…' }: { label?: string }) {
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2.5 py-12 text-sm font-semibold text-slate-500"
    >
      <span
        aria-hidden
        className="size-5 animate-spin rounded-full border-2 border-[#3851F0] border-t-transparent"
      />
      {label}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-center justify-between rounded-2xl bg-red-50 p-4 text-sm font-medium text-red-700 border border-red-200/80"
    >
      <div className="flex items-center gap-2">
        <span className="text-red-500">⚠️</span>
        <span>{errorMessage(error)}</span>
      </div>
      {onRetry ? (
        <Button variant="secondary" className="!min-h-8 !px-3 !text-xs !border-red-200" onClick={onRetry}>
          Coba lagi
        </Button>
      ) : null}
    </div>
  );
}

export function Alert({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'error';
  children: ReactNode;
}) {
  const tones = {
    info: 'bg-blue-50 text-blue-900 border-blue-200/80',
    success: 'bg-emerald-50 text-emerald-900 border-emerald-200/80',
    warning: 'bg-amber-50 text-amber-900 border-amber-200/80',
    error: 'bg-red-50 text-red-900 border-red-200/80',
  };
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-2xl p-4 text-sm font-medium border ${tones[tone]}`}
    >
      {children}
    </div>
  );
}

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  COMPLETED: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  RELEASED: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  PAID: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  SUCCESS: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  ACTIVE: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  MATCHED: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  DONE: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  ATTRIBUTED: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  REINSTATED: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  ISSUED: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  AMENDED: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200/70',
    dot: 'bg-emerald-500',
  },
  PENDING: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200/70', dot: 'bg-amber-500' },
  DRAFT: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200/70', dot: 'bg-amber-500' },
  REQUESTED: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200/70',
    dot: 'bg-amber-500',
  },
  PROCESSING: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200/70',
    dot: 'bg-amber-500',
  },
  PENDING_PAYMENT: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200/70',
    dot: 'bg-amber-500',
  },
  QUEUED: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200/70', dot: 'bg-amber-500' },
  RUNNING: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200/70', dot: 'bg-amber-500' },
  UNATTRIBUTED: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200/70',
    dot: 'bg-amber-500',
  },
  CANCELLED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200/70', dot: 'bg-red-500' },
  RETURNED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200/70', dot: 'bg-red-500' },
  VOID: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200/70', dot: 'bg-red-500' },
  EXPIRED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200/70', dot: 'bg-red-500' },
  FAILED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200/70', dot: 'bg-red-500' },
  SUSPENDED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200/70', dot: 'bg-red-500' },
  MISMATCH: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200/70', dot: 'bg-red-500' },
};

const DEFAULT_STYLE = {
  bg: 'bg-slate-100',
  text: 'text-slate-700',
  border: 'border-slate-200',
  dot: 'bg-slate-400',
};

export function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? DEFAULT_STYLE;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold border ${style.bg} ${style.text} ${style.border}`}
    >
      <span className={`size-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
      <span>{STATUS_LABEL[status] ?? status}</span>
    </span>
  );
}

export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  align?: 'right';
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  empty = 'Tidak ada data',
  caption,
  testId,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  empty?: string;
  caption: string;
  testId?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-2xl bg-white border border-slate-200/80 shadow-xs">
      <table className="min-w-full text-sm text-left" data-testid={testId}>
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-slate-50/90 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200/70 select-none">
          <tr>
            {columns.map((c) => (
              <th
                key={c.header}
                scope="col"
                className={`px-4 py-3.5 font-bold ${c.align === 'right' ? 'text-right' : ''}`}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center text-slate-400">
                <div className="flex flex-col items-center justify-center gap-2">
                  <span className="flex size-10 items-center justify-center rounded-full bg-slate-100 text-slate-400 text-sm font-bold">
                    ∅
                  </span>
                  <span className="text-sm font-medium">{empty}</span>
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)} className="hover:bg-slate-50/70 transition-colors">
                {columns.map((c) => (
                  <td
                    key={c.header}
                    className={`px-4 py-3.5 align-middle ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}
                  >
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export const PAGE_SIZES = [10, 25, 50, 100] as const;

/** Potong baris untuk halaman tertentu (paging di browser); halaman di luar jangkauan dijepit. */
export function paginate<T>(rows: T[], page: number, pageSize: number) {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = (current - 1) * pageSize;
  return { rows: rows.slice(start, start + pageSize), page: current, pageCount, start };
}

/** Nomor halaman yang ditampilkan: selalu halaman pertama/terakhir, sekitar halaman aktif, sisanya "…". */
function pageItems(page: number, pageCount: number): (number | 'gap')[] {
  const pages = new Set([1, pageCount, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pageCount));
  const sorted = [...pages].sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1]! > 1 ? ['gap' as const, p] : [p]));
}

const navClass =
  'flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-40';
const navIdle = 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50';

function PageSizeSelect({ pageSize, onPageSize }: { pageSize: number; onPageSize: (size: number) => void }) {
  const id = useId();
  return (
    <>
      <label htmlFor={id} className="font-semibold">
        Baris per halaman
      </label>
      <select
        id={id}
        value={pageSize}
        onChange={(e) => onPageSize(Number(e.target.value))}
        className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs font-bold text-slate-700 outline-none focus:border-[#3851F0]"
      >
        {PAGE_SIZES.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </>
  );
}

/**
 * Paging berbasis kursor (daftar besar seperti audit log): tanpa jumlah total, jadi hanya
 * sebelumnya/berikutnya + nomor halaman yang sedang dibuka.
 */
export function CursorPagination({
  page,
  pageSize,
  rowCount,
  hasNext,
  onFirst,
  onPrev,
  onNext,
  onPageSize,
}: {
  page: number;
  pageSize: number;
  /** Jumlah baris di halaman ini. */
  rowCount: number;
  hasNext: boolean;
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onPageSize: (size: number) => void;
}) {
  const from = rowCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + rowCount;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-3 text-xs text-slate-500">
      <div className="flex items-center gap-2">
        <PageSizeSelect pageSize={pageSize} onPageSize={onPageSize} />
        <span className="tabular-nums">
          Baris {from}–{to}
        </span>
      </div>
      <nav aria-label="Halaman tabel" className="flex items-center gap-1">
        <button
          type="button"
          className={`${navClass} ${navIdle}`}
          onClick={onFirst}
          disabled={page <= 1}
          aria-label="Halaman pertama"
        >
          «
        </button>
        <button
          type="button"
          className={`${navClass} ${navIdle}`}
          onClick={onPrev}
          disabled={page <= 1}
          aria-label="Halaman sebelumnya"
        >
          ‹ Sebelumnya
        </button>
        <span
          aria-current="page"
          className={`${navClass} border-[#3851F0] bg-[#3851F0] text-white tabular-nums`}
        >
          Halaman {page}
        </span>
        <button
          type="button"
          className={`${navClass} ${navIdle}`}
          onClick={onNext}
          disabled={!hasNext}
          aria-label="Halaman berikutnya"
        >
          Berikutnya ›
        </button>
      </nav>
    </div>
  );
}

export function Pagination({
  page,
  pageCount,
  pageSize,
  total,
  onPage,
  onPageSize,
  note,
}: {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
  /** Teks tambahan setelah ringkasan, mis. batas pratinjau. */
  note?: string;
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-3 text-xs text-slate-500">
      <div className="flex items-center gap-2">
        <PageSizeSelect pageSize={pageSize} onPageSize={onPageSize} />
        <span className="tabular-nums">
          {from}–{to} dari {total}
          {note ? ` · ${note}` : ''}
        </span>
      </div>
      <nav aria-label="Halaman tabel" className="flex items-center gap-1">
        <button
          type="button"
          className={`${navClass} ${navIdle}`}
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label="Halaman sebelumnya"
        >
          ‹
        </button>
        {pageItems(page, pageCount).map((item, i) =>
          item === 'gap' ? (
            <span key={`gap-${i}`} className="px-1 text-slate-400" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onPage(item)}
              aria-current={item === page ? 'page' : undefined}
              className={`${navClass} tabular-nums ${
                item === page ? 'border-[#3851F0] bg-[#3851F0] text-white' : navIdle
              }`}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          className={`${navClass} ${navIdle}`}
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount}
          aria-label="Halaman berikutnya"
        >
          ›
        </button>
      </nav>
    </div>
  );
}

export function Dl({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
      {items.map(([k, v], i) => (
        <div key={i} className="flex justify-between gap-3 border-b border-slate-100 py-1">
          <dt className="text-slate-600">{k}</dt>
          <dd className="text-right font-medium text-slate-900 tabular-nums">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Aksi yang wajib beralasan (PRD bagian 13/15): dialog meminta alasan ≥ 5 karakter sebelum
 * `onConfirm` dipanggil. Tombol pemicu hanya dirender pemanggil bila izin aksi ada.
 */
export function ReasonAction({
  label,
  title,
  variant = 'secondary',
  minLength = 5,
  onConfirm,
  children,
}: {
  label: string;
  title: string;
  variant?: Variant;
  minLength?: number;
  onConfirm: (reason: string) => Promise<unknown>;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const tooShort = reason.trim().length < minLength;
  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      setOpen(false);
      setReason('');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/50 backdrop-blur-xs p-4 text-left font-normal whitespace-normal animate-in fade-in"
        >
          <div className="w-full max-w-md space-y-4 rounded-2xl bg-white p-6 shadow-2xl border border-slate-100">
            <div>
              <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            </div>
            {children}
            <TextArea
              label="Alasan (wajib)"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={reason && tooShort ? `Minimal ${minLength} karakter` : undefined}
            />
            {error ? <ErrorState error={error} /> : null}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
                Batal
              </Button>
              <Button
                variant={variant === 'danger' ? 'danger' : 'primary'}
                disabled={tooShort}
                loading={busy}
                onClick={() => void confirm()}
              >
                Konfirmasi
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export function JsonView({ value }: { value: unknown }) {
  return (
    <pre className="max-h-96 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}
